import React, { useCallback, useState } from 'react';
import { Box, Button, Chip, TextField, Typography } from '@mui/material';
import { useNotify, useTranslate } from 'react-admin';
import { fieldErrors } from '../../services/platformSettingsService';
import type { PlatformSettingGroup } from '../../types';

/** Form-Startwerte: nur DB-Werte; Env-Werte erscheinen als Platzhalter (leer = Fallback). */
export const initialValues = (group: PlatformSettingGroup, keys: string[]): Record<string, string> =>
    Object.fromEntries(keys.map((k) => [k, group[k]?.source === 'db' ? group[k].value : '']));

export interface FieldDef {
    key: string;
    type?: 'text' | 'multiline' | 'number';
    select?: { value: string; label: string }[];
    wide?: boolean;
    inputMode?: 'numeric' | 'decimal';
}

interface Props {
    group: string;
    defs: FieldDef[];
    data: PlatformSettingGroup;
    labelPrefix: string;
    onSave: (values: Record<string, string>) => Promise<void>;
    children?: React.ReactNode;
}

/**
 * Generisches Formular für eine Einstellungsgruppe: leere Felder fallen auf den
 * Env-Wert zurück (als Platzhalter sichtbar), Herkunft als Chip, Feldfehler der API
 * (422) unter dem Feld.
 */
const SettingsForm: React.FC<Props> = ({ group, defs, data, labelPrefix, onSave, children }) => {
    const translate = useTranslate();
    const notify = useNotify();
    const keys = defs.map((d) => d.key);
    const [values, setValues] = useState<Record<string, string>>(() => initialValues(data, keys));
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [saving, setSaving] = useState(false);

    const set = useCallback((k: string, v: string) => setValues((prev) => ({ ...prev, [k]: v })), []);

    const save = async () => {
        setSaving(true);
        setErrors({});
        try {
            await onSave(values);
            notify(translate('platformSettings.saved'), { type: 'success' });
        } catch (err: any) {
            const fe = fieldErrors(err);
            setErrors(fe);
            notify(Object.keys(fe).length ? translate('platformSettings.invalid') : (err?.response?.data?.error ?? translate('platformSettings.saveError')), { type: 'error' });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Box>
            <Box sx={{ display: 'grid', columnGap: 2, rowGap: { xs: 3, md: 2 }, gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: '1fr 1fr' } }}>
                {defs.map((d) => {
                    const entry = data[d.key];
                    const error = errors[`${group}.${d.key}`];
                    return (
                        <Box key={d.key} sx={{ gridColumn: d.wide ? { md: '1 / -1' } : undefined }}>
                            <TextField
                                fullWidth
                                size="small"
                                select={!!d.select}
                                multiline={d.type === 'multiline'}
                                minRows={d.type === 'multiline' ? 3 : undefined}
                                label={translate(`${labelPrefix}.${d.key}`)}
                                value={values[d.key] ?? ''}
                                placeholder={entry?.source === 'env' ? entry.value : undefined}
                                onChange={(e) => set(d.key, e.target.value)}
                                error={!!error}
                                helperText={error}
                                slotProps={{
                                    inputLabel: { shrink: true },
                                    htmlInput: { inputMode: d.inputMode, 'data-testid': `field-${group}-${d.key}` },
                                    select: d.select ? { native: true } : undefined,
                                }}
                            >
                                {d.select && [<option key="" value="">{entry?.value ?? ''}</option>, ...d.select.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)]}
                            </TextField>
                            <Chip
                                size="small"
                                variant="outlined"
                                sx={{ mt: 0.5, height: 20, fontSize: 11 }}
                                data-testid={`source-${group}-${d.key}`}
                                label={translate(entry?.source === 'db' ? 'platformSettings.sourceDb' : 'platformSettings.sourceEnv')}
                            />
                        </Box>
                    );
                })}
            </Box>
            {children}
            <Box sx={{ mt: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
                <Button variant="contained" onClick={save} disabled={saving} data-testid={`save-${group}`}>
                    {translate('platformSettings.save')}
                </Button>
                <Typography variant="body2" sx={{ color: 'var(--oa-text-2)' }}>{translate('platformSettings.emptyHint')}</Typography>
            </Box>
        </Box>
    );
};

export default SettingsForm;
